import { FC, useLayoutEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import moment from 'moment';
import { ExportableHighchart } from 'components/chart-export';
import { PLOT_LINE_LABEL_ALLOW_OVERLAP } from 'components/charts/plot-line-label';

Highcharts.setOptions({ time: { useUTC: true } });
import { RadioButton, RadioButtonGroup } from '@carbon/react';
import {
  OIL_COLOR,
  GAS_COLOR,
  type PriceCurveChartProps,
  type HistoricRange,
} from './types';
import style from './price-curve-preview-chart.module.scss';

type CommodityFilter = 'both' | 'oil' | 'gas';
type RangeFilter = '12mo' | '60mo' | 'allFwd' | 'full';
type ProjectionFilter = '3yr' | '5yr' | '10yr' | '25yr' | '30yr';

const PROJECTION_MONTHS: Record<ProjectionFilter, number> = {
  '3yr': 36,
  '5yr': 60,
  '10yr': 120,
  '25yr': 300,
  '30yr': 360,
};

export const PriceCurvePreviewChart: FC<PriceCurveChartProps> = ({
  series,
  dates,
  referenceLineTs,
  filterMode,
  showHistoricControls,
  historicRange,
  onHistoricRangeChange,
  onVisibleRangeChange,
  todayLineTs,
}) => {
  const [commodity, setCommodity] = useState<CommodityFilter>('both');
  const [range, setRange] = useState<RangeFilter>('full');
  const [projection, setProjection] = useState<ProjectionFilter>('30yr');

  // Compute axis extremes based on filter mode
  const { xMin, xMax } = useMemo(() => {
    if (dates.length === 0) return { xMin: undefined, xMax: undefined };

    if (filterMode === 'projection') {
      const todayTs = todayLineTs ?? moment.utc().valueOf();
      const projMonths = PROJECTION_MONTHS[projection];
      const histMonths = historicRange ?? 0;

      // Right boundary: today + projection months (exact calendar math)
      const projEndTs = moment.utc(todayTs).add(projMonths, 'months').valueOf();
      const rightIdx = dates.findIndex((ts) => ts >= projEndTs);
      const xMaxVal =
        rightIdx >= 0 ? dates[rightIdx - 1] : dates[dates.length - 1];

      // Left boundary: today - historic months (or first date if no history)
      let xMinVal = dates[0];
      if (histMonths > 0 && todayLineTs) {
        const histStartTs = moment
          .utc(todayTs)
          .subtract(histMonths, 'months')
          .valueOf();
        const leftIdx = dates.findIndex((ts) => ts >= histStartTs);
        xMinVal = leftIdx >= 0 ? dates[leftIdx] : dates[0];
      } else if (histMonths === 0 && todayLineTs) {
        // "None" selected — start from today
        const todayIdx = dates.findIndex((ts) => ts >= todayTs);
        xMinVal = todayIdx >= 0 ? dates[todayIdx] : dates[0];
      }

      return { xMin: xMinVal, xMax: xMaxVal };
    }

    // Range mode — forward boundary starts at referenceLineTs
    const fwdStartIdx = referenceLineTs
      ? dates.findIndex((ts) => ts > referenceLineTs)
      : -1;

    switch (range) {
      case '12mo': {
        const s = fwdStartIdx >= 0 ? fwdStartIdx : 0;
        const e = Math.min(s + 12, dates.length) - 1;
        return { xMin: dates[s], xMax: dates[e] };
      }
      case '60mo': {
        const s = fwdStartIdx >= 0 ? fwdStartIdx : 0;
        const e = Math.min(s + 60, dates.length) - 1;
        return { xMin: dates[s], xMax: dates[e] };
      }
      case 'allFwd': {
        const s = fwdStartIdx >= 0 ? fwdStartIdx : 0;
        return {
          xMin: dates[s],
          xMax: dates[dates.length - 1],
        };
      }
      case 'full':
      default:
        return { xMin: undefined, xMax: undefined };
    }
  }, [
    dates,
    referenceLineTs,
    filterMode,
    range,
    projection,
    todayLineTs,
    historicRange,
  ]);

  useLayoutEffect(() => {
    onVisibleRangeChange?.(xMin, xMax);
  }, [xMin, xMax, onVisibleRangeChange]);

  const showOil = commodity === 'both' || commodity === 'oil';
  const showGas = commodity === 'both' || commodity === 'gas';

  const chartOptions = useMemo((): Highcharts.Options => {
    const yAxis: Highcharts.YAxisOptions[] = [
      {
        title: { text: '$/BBL' },
        labels: { style: { color: OIL_COLOR } },
        visible: showOil,
      },
      {
        title: { text: '$/MMBTU' },
        opposite: showOil && showGas,
        labels: { style: { color: GAS_COLOR } },
        visible: showGas,
      },
    ];

    const chartSeries: Highcharts.SeriesSplineOptions[] = [
      {
        type: 'spline',
        yAxis: 0,
        name: 'Oil ($/BBL)',
        color: OIL_COLOR,
        data: series.oil,
        visible: showOil,
        showInLegend: showOil,
      },
      {
        type: 'spline',
        yAxis: 1,
        name: 'Gas ($/MMBTU)',
        color: GAS_COLOR,
        data: series.gas,
        visible: showGas,
        showInLegend: showGas,
      },
    ];

    if (series.valOil) {
      chartSeries.push({
        type: 'spline',
        yAxis: 0,
        name: series.valOilLabel ?? 'Val. oil',
        color: OIL_COLOR,
        dashStyle: 'DashDot',
        data: series.valOil,
        visible: series.valVisible ?? false,
        showInLegend: showOil,
      });
    }

    if (series.valGas) {
      chartSeries.push({
        type: 'spline',
        yAxis: 1,
        name: series.valGasLabel ?? 'Val. gas',
        color: GAS_COLOR,
        dashStyle: 'DashDot',
        data: series.valGas,
        visible: series.valVisible ?? false,
        showInLegend: showGas,
      });
    }

    const plotLines: Highcharts.XAxisPlotLinesOptions[] = [];
    if (referenceLineTs !== undefined) {
      plotLines.push({
        value: referenceLineTs,
        color: '#6f6f6f',
        dashStyle: 'Dash',
        width: 1.5,
        zIndex: 3,
        label: {
          ...PLOT_LINE_LABEL_ALLOW_OVERLAP,
          text: 'Strip Date',
          rotation: 0,
          align: 'left',
          x: 5,
          verticalAlign: 'top',
          y: 10,
          style: { fontSize: '11px', color: '#6f6f6f' },
        },
      });
    }

    if (todayLineTs !== undefined && (historicRange ?? 0) > 0) {
      plotLines.push({
        value: todayLineTs,
        color: '#6f6f6f',
        dashStyle: 'Dash',
        width: 1.5,
        zIndex: 3,
        label: {
          ...PLOT_LINE_LABEL_ALLOW_OVERLAP,
          text: 'Today',
          rotation: 0,
          align: 'left',
          x: 5,
          verticalAlign: 'top',
          y: 10,
          style: { fontSize: '11px', color: '#6f6f6f' },
        },
      });
    }

    return {
      credits: { enabled: false },
      chart: {
        type: 'spline',
        height: 400,
        animation: { duration: 500 },
      },
      title: { text: undefined },
      xAxis: {
        type: 'datetime',
        title: { text: undefined },
        min: xMin,
        max: xMax,
        plotLines,
      },
      yAxis,
      tooltip: {
        shared: true,
        xDateFormat: '%B %Y',
        valueDecimals: 2,
      },
      plotOptions: {
        spline: {
          marker: { enabled: false },
          lineWidth: 2,
        },
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
      },
      series: chartSeries,
    };
  }, [
    series,
    referenceLineTs,
    todayLineTs,
    showOil,
    showGas,
    xMin,
    xMax,
    historicRange,
  ]);

  const controls = (
    <div className={style.controlsRow}>
      <div className={style.controlGroup}>
        <span className={style.controlLabel}>Commodity</span>
        <RadioButtonGroup
          legendText=""
          name="commodity"
          orientation="horizontal"
          valueSelected={commodity}
          onChange={(value: string | number | undefined) =>
            setCommodity(value as CommodityFilter)
          }
        >
          <RadioButton id="commodity-both" value="both" labelText="Both" />
          <RadioButton id="commodity-oil" value="oil" labelText="Oil" />
          <RadioButton id="commodity-gas" value="gas" labelText="Gas" />
        </RadioButtonGroup>
      </div>
      <div className={style.controlGroup}>
        {filterMode === 'range' ? (
          <>
            <span className={style.controlLabel}>Range</span>
            <RadioButtonGroup
              legendText=""
              name="range"
              orientation="horizontal"
              valueSelected={range}
              onChange={(value: string | number | undefined) =>
                setRange(value as RangeFilter)
              }
            >
              <RadioButton id="range-12mo" value="12mo" labelText="12 mo" />
              <RadioButton id="range-60mo" value="60mo" labelText="60 mo" />
              <RadioButton
                id="range-allFwd"
                value="allFwd"
                labelText="All fwd"
              />
              <RadioButton id="range-full" value="full" labelText="Full" />
            </RadioButtonGroup>
          </>
        ) : (
          <>
            <span className={style.controlLabel}>Projection</span>
            <RadioButtonGroup
              legendText=""
              name="projection"
              orientation="horizontal"
              valueSelected={projection}
              onChange={(value: string | number | undefined) =>
                setProjection(value as ProjectionFilter)
              }
            >
              <RadioButton id="proj-3yr" value="3yr" labelText="3 yr" />
              <RadioButton id="proj-5yr" value="5yr" labelText="5 yr" />
              <RadioButton id="proj-10yr" value="10yr" labelText="10 yr" />
              <RadioButton id="proj-25yr" value="25yr" labelText="25 yr" />
              <RadioButton id="proj-30yr" value="30yr" labelText="30 yr" />
            </RadioButtonGroup>
          </>
        )}
      </div>
      {showHistoricControls && onHistoricRangeChange && (
        <div className={style.controlGroup}>
          <span className={style.controlLabel}>Historic Data</span>
          <RadioButtonGroup
            legendText=""
            name="historic"
            orientation="horizontal"
            valueSelected={String(historicRange ?? 0)}
            onChange={(value: string | number | undefined) =>
              onHistoricRangeChange(Number(value) as HistoricRange)
            }
          >
            <RadioButton id="hist-none" value="0" labelText="None" />
            <RadioButton id="hist-1yr" value="12" labelText="1 yr" />
            <RadioButton id="hist-5yr" value="60" labelText="5 yr" />
            <RadioButton id="hist-10yr" value="120" labelText="10 yr" />
          </RadioButtonGroup>
        </div>
      )}
    </div>
  );

  return (
    <ExportableHighchart
      options={chartOptions}
      exportConfig={{ label: 'Price Curve', filenamePrefix: 'price-curve' }}
      header={controls}
    />
  );
};
